import type { Team } from '@compass/client/types/team';
import { organizationTeamService } from '@services/api';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { buildAssigneeGroups, type UseAssigneeOptionsReturn } from '@/hooks/useAssigneeOptions';
import { useTeamMembersMap } from '@/hooks/useTeams';

// Single page is enough for realistic org team counts; pagination via
// start_after_id can be added if an org ever exceeds this.
const ORG_TEAMS_LIMIT = 1000;

/**
 * Org-wide assignee options for the Tier-2 cross-program cases page.
 *
 * Mirrors `useAssigneeOptions` but sources teams from `ListOrganizationTeams`
 * (across the whole org, backend-authorized to the caller's accessible programs)
 * instead of the single selected program's teams — so the assignee filter covers
 * every program the org-wide case list can span.
 */
export const useOrgAssigneeOptions = (): UseAssigneeOptionsReturn => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const { data, isLoading: teamsLoading } = useQuery({
    queryKey: ['compass', 'tier2', 'org-teams', selectedOrganizationId || ''],
    queryFn: () =>
      organizationTeamService.listOrganizationTeams({
        organization_id: selectedOrganizationId!,
        limit: ORG_TEAMS_LIMIT,
      }),
    enabled: !!selectedOrganizationId,
    staleTime: 5 * 60 * 1000,
  });

  const teams = useMemo<Team[]>(() => data?.teams ?? [], [data?.teams]);
  const { usersMap, isLoading: membersLoading } = useTeamMembersMap(teams);

  const { groups, allUserIds } = useMemo(
    () => buildAssigneeGroups(teams, usersMap),
    [teams, usersMap],
  );

  return { groups, allUserIds, usersMap, isLoading: teamsLoading || membersLoading };
};
